import React, { useEffect, useRef, useState } from "react";
import axios, { AxiosResponse } from "axios";
import { DataTableWithoutButtons } from "components/DataTable/DataTableWithoutButtons";
import { AfDataType } from "utils/TableColumnData";
import handleWheel from "utils/handleOnWheel";
import { DeleteCommanWay, ListCommanWay, UpdateCommanWay } from "API/commonApi";
import { UseMutateFunction, useMutation, useQuery } from "@tanstack/react-query";
import { Img } from "components";
import moment from "moment";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import Select from "react-select";
import { roles } from "utils/constant";
import { utils, write, writeFile } from "xlsx";
import { saveAs } from "file-saver";
import { Popover, Transition } from "@headlessui/react";
import { ChevronDownIcon, DocumentDuplicateIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Checkbox } from "@heroui/react";

const PaymentStatement: React.FC<any> = ({ customerId, searchIncrement, selectedInvoice, setSelectedInvoice, setInvoiceTableData, tableRef }) => {
  const ADT = AfDataType;

  const [tableData, setTableData] = useState<any>([]);
  const [tableDetails, setTableDetails] = useState<any>([]);

  const [startDate, setStartDate] = useState<any>(moment().startOf("day").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState<any>(moment().endOf("day").format("YYYY-MM-DD"));
  const [query, setQuery] = useState<any>({
    fromDate: moment().startOf("day"),
    toDate: moment().endOf("day"),
  });
  const [isDataAdded, setIsDataAdded] = useState(false);

  const [allAgents, setAllAgents] = useState([]);

  const [totalCredit, setTotalCredit] = useState(0);
  const [totalTdsCredit, setTotalTdsCredit] = useState(0);
  const [pendingAmount, setPendingAmount] = useState(0);

  const [invoiceWisePayment, setInvoiceWisePayment] = useState({});
  const [selectedSum, setSelectedSum] = useState<number | null>(null);
  const [showSum, setShowSum] = useState(false);
  const [selectedCellsSet, setSelectedCellsSet] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (searchIncrement > 0 && customerId) {
      //console.log("Search Increment Changed:", searchIncrement, customerId);
      setIsDataAdded(!isDataAdded);
      setQuery({
        /*fromDate: startDate ? moment(startDate).startOf("day") : moment().startOf("day"),
        toDate: endDate ? moment(endDate).endOf("day") : moment().endOf("day"),*/
        customer: customerId,
      });
    }
  }, [searchIncrement]);

  const { isFetching, isLoading, refetch } = useQuery(
    ["admin_statementp_report", query.customer, searchIncrement],
    () => ListCommanWay("/admin/collection/list", { customer: query.customer }, { include: ["_addedBy"], paginate: 10000 }),
    {
      keepPreviousData: false,
      enabled: !!query.customer && searchIncrement > 0,
      onSuccess: async (paymentResponse) => {
        if (paymentResponse.status !== "SUCCESS") {
          setTableData([]);
          setInvoiceWisePayment({});
          return;
        }

        try {
          const invoiceResponse = await ListCommanWay(
            "/admin/inovic_for_c_b/list",
            { customerId: query.customer, fromDate: { $gte: "2024-04-01" } },
            { include: ["_subbranch"], paginate: 10000 }
          );

          if (invoiceResponse.status !== "SUCCESS") {
            setTableData([]);
            return;
          }

          const payments = paymentResponse.data.data;
          const invoices = invoiceResponse.data.data;
          setInvoiceTableData(invoices);

          // Step 1: Build invoice map
          const invoiceMap: any = {};

          invoices.forEach((inv: any) => {
            const invId = inv.id;
            invoiceMap[invId] = {
              invoiceId: invId,
              invoiceNo: inv._subbranch?.branch_code + "" + inv.invoice_no,
              invoiceAmount: Number(inv.total_value || 0),
              allocated: 0,
              pending: Number(inv.total_value || 0),
              invDate: inv.invDate,
              payments: [],
            };
          });

          // Step 2: Process each payment and allocate to linked invoices
          let totalReceived = 0;

          payments.forEach((payment: any) => {
            const paymentAmount = Number(payment.totalAmount || 0);
            const basicAmount = Number(payment.basicAmount || 0);
            const tdsPercent = Number(payment.tds || 0);

            const tdsAmount = Math.round(basicAmount * (tdsPercent / 100)); // e.g., 900149 * 2 / 100 = 18003
            const totalEffectiveAmount = paymentAmount + tdsAmount; // Total that covers invoice(s)

            const linkedInvoices: number[] = payment.invoiceJson?.invoices || [];
            const paymentDate = payment.payRecvDate;
            const paymentId = payment.id;
            const remark = payment.remark || "";

            totalReceived += paymentAmount;

            if (linkedInvoices.length === 0) {
              // On-account payment (not linked) → skip or handle later
              return;
            }

            // Calculate total pending across linked invoices to determine allocation ratio
            const totalPendingForThisPayment = linkedInvoices.reduce((sum, invId) => {
              return sum + (invoiceMap[invId]?.pending || 0);
            }, 0);

            // Allocate proportionally based on each invoice's pending amount
            linkedInvoices.forEach((invId) => {
              const inv = invoiceMap[invId];
              if (!inv || inv.pending <= 0) return;

              // Calculate this invoice's share of the payment
              const ratio = inv.pending / totalPendingForThisPayment;
              const allocatePayment = Number((paymentAmount * ratio).toFixed(2));
              const allocateTds = Number((tdsAmount * ratio).toFixed(2));
              const allocateTotal = allocatePayment + allocateTds;

              inv.allocated += allocatePayment;
              inv.pending -= allocateTotal; // Reduce by total effective amount (payment + TDS)

              inv.payments.push({
                paymentId,
                amount: allocatePayment,
                recvdTds: allocateTds,
                date: paymentDate,
                remark,
              });
            });
          });

          // Final invoice-wise allocation
          setInvoiceWisePayment(invoiceMap);

          // Step 3: Build ledger table (your original logic)
          let tdata: any[] = [];
          let runningBalance = 0;

          // Add Credits (Payments Received)
          payments.forEach((c: any) => {
            const date = moment(c.payRecvDate, "DD-MM-YYYY HH:mm:ss").toDate();
            tdata.push({
              type: "CREDIT",
              date,
              amount: Number(c.totalAmount || 0),
              tds: Math.round((c.basicAmount * c.tds) / 100) || 0,
              remark: c.remark || "",
              paymentId: c.id,
              payments: c.invoiceJson?.invoices || [],
              addedBy: c._addedBy?.name || "",
              createdAt: c.createdAt,
            });
            runningBalance += Number(c.totalAmount || 0);
          });

          // Add Debits (Invoices)
          invoices.forEach((c: any) => {
            const date = moment(c.invDate).toDate();
            tdata.push({
              type: "DEBIT",
              date,
              amount: Number(c.total_value || 0),
              remark: c.utrNo || "",
              invoiceNo: c._subbranch?.branch_code + "" + c.invoice_no,
              invoiceId: c.id,
              fromDate: moment(c.fromDate).format("DD-MM-YYYY"),
              toDate: moment(c.toDate).format("DD-MM-YYYY"),
              followupDate:
                c.payDate && moment(c.payDate, "DD-MM-YYYY HH:mm:ss").isValid()
                  ? moment(c.payDate, "DD-MM-YYYY HH:mm:ss").format("DD-MM-YYYY")
                  : "-",
            });
            runningBalance -= Number(c.total_value || 0);
          });

          // Sort by date
          tdata.sort((a, b) => a.date - b.date);

          // Assign status (Cleared / Pending) based on running balance
          let balance = 0;
          let tdsCredit = 0;
          tdata = tdata.map((item) => {
            if (item.type === "CREDIT") {
              balance += item.amount;
            } else {
              balance -= item.amount;
              item.allocatedAmount = invoiceMap[item.invoiceId]?.allocated || 0;

              item.effRcvd = invoiceMap[item.invoiceId]
                ? invoiceMap[item.invoiceId].payments.reduce((sum: number, p: any) => sum + (p.amount || 0) + (p.recvdTds || 0), 0)
                : 0;

              tdsCredit += invoiceMap[item.invoiceId]
                ? invoiceMap[item.invoiceId].payments.reduce((sum: number, p: any) => sum + (p.recvdTds || 0), 0)
                : 0;

              //console.log("Pending for Invoice ", item.invoiceNo, "is", invoiceMap[item.invoiceId]?.pending);

              // Store the actual outstanding from invoiceMap which is correctly calculated
              item.outstanding = invoiceMap[item.invoiceId]?.pending || 0;

              item.status = Math.abs(invoiceMap[item.invoiceId]?.pending || 0) <= 5 ? "Cleared" : "Pending";
            }
            item.runningBalance = balance;
            return item;
          });

          const totalInvoiceAmount = invoices.reduce((sum: number, inv: any) => sum + Number(inv.total_value || 0), 0);
          setPendingAmount(totalInvoiceAmount - totalReceived - tdsCredit);
          setTotalCredit(totalReceived);
          setTotalTdsCredit(tdsCredit);
          console.log(tdata);
          setTableData(tdata);

          console.log("Invoice Wise Payment Allocation:", invoiceMap);
          // Use invoiceWisePayment anywhere now
        } catch (err) {
          console.error("Error fetching invoices:", err);
          setTableData([]);
        }
      },
      onError: () => {
        setTableData([]);
        setInvoiceWisePayment({});
      },
    }
  );

  // Delete collection/payment mutation
  const { mutate: deleteCollection, isLoading: isDeleting } = useMutation(
    (paymentId: number) => DeleteCommanWay({ url: `/admin/collection/delete/${paymentId}`, data: {} }),
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          UseToast("Payment deleted successfully!", "success");
          refetch();
        } else {
          UseToast(data.message || "Failed to delete payment", "error");
        }
      },
      onError: () => {
        UseToast("Failed to delete payment", "error");
      },
    }
  );

  const handleDeletePayment = (paymentId: number) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteCollection(paymentId);
      }
    });
  };

  const BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const userD = (() => { const auth = localStorage.getItem("auth"); return auth ? JSON.parse(auth) : {}; })();
  const [userDetails, setUserDetails] = useState<any>(userD?.state?.user || {});

  const handleMouseUp = () => {
    const selection = window.getSelection();

    // Clear previous selection highlighting
    const table = tableRef.current;
    if (table) {
      const allCells = (table as HTMLElement).querySelectorAll("td");
      allCells.forEach((cell) => cell.classList.remove("cell-selected"));
    }

    if (!selection || selection.toString().trim() === "") {
      setShowSum(false);
      setSelectedCellsSet(new Set());
      return;
    }

    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;

    // Check if selection is within the table
    let node: any = container;
    let isInTable = false;
    while (node) {
      if (node === table) {
        isInTable = true;
        break;
      }
      node = node.parentNode;
    }

    if (!isInTable) {
      setShowSum(false);
      setSelectedCellsSet(new Set());
      return;
    }

    // Get all selected cells
    const selectedCells: HTMLElement[] = [];

    // Find start and end TD cells
    let startNode: any = range.startContainer;
    while (startNode && startNode.tagName !== "TD" && startNode !== table) {
      startNode = startNode.parentElement;
    }

    let endNode: any = range.endContainer;
    while (endNode && endNode.tagName !== "TD" && endNode !== table) {
      endNode = endNode.parentElement;
    }

    if (!startNode || !endNode || startNode.tagName !== "TD" || endNode.tagName !== "TD") {
      setShowSum(false);
      return;
    }

    // Get row and column indices for start and end cells
    const startRow = startNode.parentElement;
    const endRow = endNode.parentElement;
    const startColIndex = Array.from(startRow.children).indexOf(startNode);
    const endColIndex = Array.from(endRow.children).indexOf(endNode);

    // Get all rows in tbody
    const tbody = (table as HTMLElement).querySelector("tbody");
    if (!tbody) return;

    const allRows = Array.from(tbody.querySelectorAll("tr")) as HTMLElement[];
    const startRowIndex = allRows.indexOf(startRow);
    const endRowIndex = allRows.indexOf(endRow);

    const minRow = Math.min(startRowIndex, endRowIndex);
    const maxRow = Math.max(startRowIndex, endRowIndex);
    const minCol = Math.min(startColIndex, endColIndex);
    const maxCol = Math.max(startColIndex, endColIndex);

    // Select cells in the rectangular range
    for (let rowIdx = minRow; rowIdx <= maxRow; rowIdx++) {
      const row = allRows[rowIdx];
      const cells = Array.from(row.children) as HTMLElement[];

      for (let colIdx = minCol; colIdx <= maxCol; colIdx++) {
        const cell = cells[colIdx];
        if (cell && cell.tagName === "TD") {
          // Only include amount columns: 4=DEBIT, 5=CREDIT, 6=TDS, 7=Eff.Rcvd, 8=Outstanding
          if ([6, 7, 8, 9, 10].includes(colIdx)) {
            selectedCells.push(cell);
          }
        }
      }
    }

    // Extract numeric values from amount-related columns and apply highlighting
    let sum = 0;
    let hasValidNumbers = false;

    selectedCells.forEach((cell) => {
      cell.classList.add("cell-selected");
      const text = cell.textContent?.trim() || "";
      // Remove commas and parse
      const numText = text.replace(/,/g, "");
      const num = parseFloat(numText);
      if (!isNaN(num)) {
        sum += num;
        hasValidNumbers = true;
      }
    });

    if (hasValidNumbers && selectedCells.length > 1) {
      setSelectedSum(sum);
      setShowSum(true);
    } else {
      setShowSum(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mousedown", () => {
      // Clear selection on new mouse down
      const table = tableRef.current;
      if (table) {
        const allCells = (table as HTMLElement).querySelectorAll("td");
        allCells.forEach((cell) => cell.classList.remove("cell-selected"));
      }
    });

    // Inject styles directly into head
    const styleId = "payment-statement-selection-style";
    let styleElement = document.getElementById(styleId) as HTMLStyleElement;

    if (!styleElement) {
      styleElement = document.createElement("style");
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    styleElement.textContent = `
      #myTable tbody td:nth-child(7),
      #myTable tbody td:nth-child(8),
      #myTable tbody td:nth-child(9),
      #myTable tbody td:nth-child(10),
      #myTable tbody td:nth-child(11) {
        cursor: cell !important;
        user-select: text !important;
        -webkit-user-select: text !important;
        -moz-user-select: text !important;
        -ms-user-select: text !important;
      }
      #myTable ::selection {
        background-color: transparent !important;
        color: inherit !important;
      }
      #myTable ::-moz-selection {
        background-color: transparent !important;
        color: inherit !important;
      }
      #myTable tbody td.cell-selected {
        background-color: #3b82f6 !important;
        color: white !important;
      }
    `;

    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
      if (styleElement && styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
    };
  }, []);

  return (
    <>
      {showSum && selectedSum !== null && (
        <div className="absolute top-16 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 font-semibold">
          Sum: {selectedSum.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      )}
      <div>
        <table className="w-full table- mb-4 text-sm" id="myTable" ref={tableRef}>
          <thead>
            <tr>
              <th className="border border-slate-200 ignore-excel">-</th>
              <th className="border border-slate-200">Sr.</th>
              <th className="border border-slate-200">From-To</th>
              <th className="border border-slate-200">Invoice Date</th>
              <th className="border border-slate-200">
                Pay Recv
                <br />
                Date
              </th>
              <th className="border border-slate-200">Invoice No</th>
              <th className="border border-slate-200">DEBIT</th>
              <th className="border border-slate-200">CREDIT</th>
              <th className="border border-slate-200">TDS</th>
              <th className="border border-slate-200">
                Eff.
                <br />
                Rcvd
              </th>
              <th className="border border-slate-200">
                Invoice
                <br />
                Outstanding
              </th>
              <th className="border border-slate-200">Status</th>
              <th className="border border-slate-200">Days</th>
              <th className="border border-slate-200">Remark</th>
              <th className="border border-slate-200">Followup Date</th>
              <th className="border border-slate-200">Added By</th>
              <th className="border border-slate-200">Added On</th>
              <th className="border border-slate-200 ignore-excel">Delete</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((r, i) => {
              return (
                <tr
                  key={"asdasd" + i}
                  className={`${r["status"] === "Cleared" ? "bg-green-300" : ""} ${r["status"] === "Pending" ? "bg-yellow-100/50" : ""} ${
                    r.type === "CREDIT" ? "bg-slate-100" : ""
                  }`}
                >
                  <th className="border border-slate-200 px-1 text-center ignore-excel">
                    {r.type == "DEBIT" && r.invoiceId ? (
                      <Checkbox
                        isSelected={selectedInvoice.includes(r.invoiceId)}
                        onValueChange={() => {
                          if (selectedInvoice.includes(r.invoiceId)) {
                            setSelectedInvoice(selectedInvoice.filter((id) => id !== r.invoiceId));
                          } else {
                            console.log("Adding invoiceId:", selectedInvoice, r.invoiceId);
                            setSelectedInvoice([...selectedInvoice, r.invoiceId]);
                          }
                        }}
                      ></Checkbox>
                    ) : (
                      "-"
                    )}
                  </th>
                  <td className="border border-slate-200 px-4 text-center">{i + 1}</td>
                  <td className="border border-slate-200 px-2 text-center">
                    {r.fromDate && r.toDate ? r.fromDate + " --to-- " + r.toDate : ""}
                  </td>
                  <td className="border border-slate-200 px-2 text-center">
                    {r.type == "DEBIT" ? moment(r.date).format("DD-MM-YYYY") : ""}
                  </td>
                  <td className="border border-slate-200 px-2 text-center">
                    {r.type == "CREDIT" ? moment(r.date).format("DD-MM-YYYY") : ""}
                  </td>

                  <td className="border border-slate-200 px-2 text-center font-RobotoCondensed">
                    {r.invoiceNo}
                    {r.type == "CREDIT" && r.payments.length > 1 ? (
                      <Popover>
                        {({ open }) => (
                          <>
                            <Popover.Button className={"flex items-center"}>
                              Show
                              <ChevronDownIcon className="ui-open:rotate-180 ui-open:transform h-4" />
                            </Popover.Button>
                            <Transition
                              show={open}
                              enter="transition duration-100 ease-out"
                              enterFrom="transform scale-95 opacity-0"
                              enterTo="transform scale-100 opacity-100"
                              leave="transition duration-75 ease-out"
                              leaveFrom="transform scale-100 opacity-100"
                              leaveTo="transform scale-95 opacity-0"
                            >
                              <Popover.Panel
                                className={
                                  "absolute bg-white p-2 shadow-2xl border-2 border-secondary1 rounded-md -translate-x-full -translate-y-full flex flex-col gap-1"
                                }
                              >
                                {r.payments?.map((p, i) => {
                                  return <span key={"pay-state-1inv" + i}>{invoiceWisePayment[p]?.invoiceNo}</span>;
                                })}
                              </Popover.Panel>
                            </Transition>
                          </>
                        )}
                      </Popover>
                    ) : (
                      r.payments?.map((p, i) => {
                        return <span key={"pay-state-2inv" + i}>{invoiceWisePayment[p]?.invoiceNo}</span>;
                      })
                    )}

                    <button
                      type="button"
                      onClick={async () => {
                        await navigator.clipboard.writeText(
                          r.invoiceNo
                            ? r.invoiceNo
                            : r.type == "CREDIT" && r.payments.length > 1
                            ? r.payments?.map((p: any) => invoiceWisePayment[p]?.invoiceNo).join(", ")
                            : r.payments?.map((p: any) => invoiceWisePayment[p]?.invoiceNo).join(", ")
                        );
                        UseToast("Copied!");
                      }}
                    >
                      <DocumentDuplicateIcon className="h-5 -mb-1 ml-2" />
                    </button>
                  </td>
                  <td className="border border-slate-200 px-2 text-right">
                    {r.type == "DEBIT" ? Number(r.amount || 0)?.toLocaleString("en-IN") : ""}
                  </td>
                  <td className="border border-slate-200 px-2 text-right">
                    {r.type == "CREDIT" ? Number(r.amount || 0)?.toLocaleString("en-IN") : ""}
                  </td>
                  <td className="border border-slate-200 px-2 text-right">
                    {r.type == "CREDIT" ? Number(r.tds || 0)?.toLocaleString("en-IN") : ""}
                  </td>
                  <td className="border border-slate-200 px-2 text-right">
                    {r.type == "DEBIT" ? Number(r.effRcvd || 0)?.toLocaleString("en-IN") : ""}
                  </td>
                  <td className="border border-slate-200 px-2 text-right">
                    {r.type == "DEBIT" ? Number(r.outstanding || 0)?.toLocaleString("en-IN") : ""}
                  </td>
                  <td className="border border-slate-200 px-2 text-center">{r.status}</td>
                  <td className="border border-slate-200 px-2 text-center">{r.type == "DEBIT" && moment().diff(moment(r.date), "days")}</td>
                  <td className="border border-slate-200 px-2 text-center">{r.remark}</td>
                  <td className="border border-slate-200 px-2 text-center">{r.followupDate}</td>
                  <td className="border border-slate-200 px-2 text-center">{r.addedBy}</td>
                  <td className="border border-slate-200 px-2 text-center">{r.createdAt}</td>
                  <td className="border border-slate-200 px-2 text-center ignore-excel">
                    {r.type === "CREDIT" && r.paymentId && (
                      <button
                        type="button"
                        onClick={() => handleDeletePayment(r.paymentId)}
                        className="text-red-500 hover:text-red-700 disabled:opacity-50"
                        disabled={isDeleting}
                        title="Delete Payment"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="font-inter">
              <td className="border border-slate-200 px-4 text-right font-semibold py-2 bg-purple-200" colSpan={6}>
                Total
              </td>
              <td className="border border-slate-200 px-2 text-right font-semibold py-2 bg-purple-200">
                {(totalCredit + totalTdsCredit + pendingAmount).toLocaleString("en-IN")}
              </td>
              <td className="border border-slate-200 px-2 text-right font-semibold py-2 bg-purple-200">
                {totalCredit.toLocaleString("en-IN")}
              </td>
              <td className="border border-slate-200 px-2 text-right font-semibold py-2 bg-purple-200">
                {totalTdsCredit.toLocaleString("en-IN")}
              </td>
              <td className="border border-slate-200 px-2 text-right font-semibold py-2 bg-purple-200" colSpan={9}></td>
            </tr>

            <tr className="font-inter">
              <td className="border border-slate-200 px-4 text-right font-semibold py-2 bg-blue-200" colSpan={6}>
                Pending Amount
              </td>
              <td className="border border-slate-200 px-2 text-center font-semibold py-2 bg-blue-200" colSpan={2}>
                {pendingAmount.toLocaleString("en-IN")}
              </td>
              <td className="border border-slate-200 px-2 text-right font-semibold py-2 bg-blue-200" colSpan={10}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
};

export default PaymentStatement;
